import { useEffect, useMemo, useState } from "react";
import { MarkerType, ReactFlowProvider, type Edge, type Node } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";
import { Canvas, Controls } from "@altimateai/lego";
import AutoTuneTaskNode, { type RFNodeData } from "./AutoTuneTaskNode";
import { computeNodeWidth, MIN_NODE_WIDTH, resetMeasureCache } from "./dagUtils";
import type {
  AutoTuneTaskNodeData,
  ClusterSpec,
  TaskEdge,
  TaskNodeStatus,
} from "./AutoTuneJobDag.types";

export type { AutoTuneTaskNodeData, ClusterSpec, TaskEdge, TaskNodeStatus };

interface AutoTuneJobDagProps {
  nodes: AutoTuneTaskNodeData[];
  edges: TaskEdge[];
  onToggle: (taskKey: string, next: boolean) => void;
  /** Selection state — when provided, each card with a matching
   *  ``taskKey`` is rendered as selected. Used by the Settings drawer
   *  so click-on-card adds/removes the task from the apply set. */
  selectedKeys?: Set<string>;
  /** Click-on-card handler. When provided, the card body becomes
   *  interactive and calls back with the new selection state. */
  onSelect?: (taskKey: string, next: boolean) => void;
  /** Caps the initial fit-to-view zoom. Single-node consumers (the
   *  cluster recommended-state DAG) pass a value < 1 so the lone card
   *  renders one step smaller with breathing room; omitted for the jobs
   *  DAG so its multi-node fit-to-view is unchanged. */
  fitViewMaxZoom?: number;
}

// Card height reserved in the dagre layout. Width is per-node (see
// ``computeNodeWidth``): each card is sized to fit its instance names on
// one line, and that same width is fed to dagre below so ranks never
// overlap.
const NODE_HEIGHT = 220;

type RFNode = Node<RFNodeData, "autoTuneTask">;

const nodeTypes = { autoTuneTask: AutoTuneTaskNode };

// Single-pass dagre layout: deterministic LR positioning that scales
// from 1 to ~50 nodes without manual tuning. Recomputed only when
// inputs change.
const layoutGraph = (
  data: AutoTuneTaskNodeData[],
  edges: TaskEdge[],
  onToggle: (taskKey: string, next: boolean) => void,
  selectedKeys?: Set<string>,
  onSelect?: (taskKey: string, next: boolean) => void
): { nodes: RFNode[]; edges: Edge[] } => {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "LR", nodesep: 60, ranksep: 100 });

  const widthById = new Map(data.map(n => [n.id, computeNodeWidth(n)]));
  data.forEach(n =>
    g.setNode(n.id, { width: widthById.get(n.id) ?? MIN_NODE_WIDTH, height: NODE_HEIGHT })
  );
  edges.forEach(e => g.setEdge(e.source, e.target));
  dagre.layout(g);

  const positionedNodes: RFNode[] = data.map(n => {
    const { x, y } = g.node(n.id);
    const width = widthById.get(n.id) ?? MIN_NODE_WIDTH;
    // ``n`` may already carry a ``selected`` flag stamped by the
    // container (drawer-driven flow). Only fall back to ``selectedKeys``
    // if the caller went through the prop path; never zero out the
    // node-level flag with ``?? false`` — that was clobbering the
    // container's selection so every card rendered as deselected
    // (muted-dashed) regardless of the actual server state.
    const stamped = (n as AutoTuneTaskNodeData & { selected?: boolean }).selected;
    const isSelected = selectedKeys ? selectedKeys.has(n.taskKey) : (stamped ?? false);
    return {
      id: n.id,
      type: "autoTuneTask",
      position: { x: x - width / 2, y: y - NODE_HEIGHT / 2 },
      data: {
        ...n,
        width,
        onToggle,
        selected: isSelected,
        onSelect,
      },
    };
  });

  // Arrowheads on edges so the dependency direction reads at a glance.
  const rfEdges: Edge[] = edges.map(e => ({
    id: `${e.source}->${e.target}`,
    source: e.source,
    target: e.target,
    type: "smoothstep",
    markerEnd: { type: MarkerType.ArrowClosed, width: 18, height: 18 },
    animated: false,
  }));

  return { nodes: positionedNodes, edges: rfEdges };
};

const AutoTuneJobDagInner = ({
  nodes,
  edges,
  onToggle,
  selectedKeys,
  onSelect,
  fitViewMaxZoom,
}: AutoTuneJobDagProps) => {
  // ``computeNodeWidth`` measures instance-name text with the value font.
  // If the first layout runs before the web font (Lexend) finishes
  // loading, measurement uses fallback metrics and cards come out too
  // narrow. Always wait for ``document.fonts.ready`` (it resolves
  // immediately when the font is already cached), drop the stale measuring
  // context, then re-run the layout so widths match what's drawn.
  const [fontsReady, setFontsReady] = useState(
    () => typeof document === "undefined" || !document.fonts
  );
  useEffect(() => {
    if (typeof document === "undefined" || !document.fonts) return;
    let active = true;
    document.fonts.ready.then(() => {
      if (!active) return;
      resetMeasureCache();
      setFontsReady(true);
    });
    return () => {
      active = false;
    };
  }, []);

  const flow = useMemo(
    () => layoutGraph(nodes, edges, onToggle, selectedKeys, onSelect),
    // ``fontsReady`` isn't read inside ``layoutGraph`` but must stay in the
    // deps: flipping it re-runs the measured layout once the value font
    // has loaded (see the effect above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodes, edges, onToggle, selectedKeys, onSelect, fontsReady]
  );
  return (
    <Canvas
      nodes={flow.nodes}
      edges={flow.edges}
      nodeTypes={nodeTypes}
      // Cap the initial fit zoom for single-node consumers so the lone
      // card isn't blown up to fill the canvas. Undefined for the jobs
      // DAG → ReactFlow's default fit (unchanged multi-node behavior).
      fitViewOptions={fitViewMaxZoom != null ? { maxZoom: fitViewMaxZoom } : undefined}
      proOptions={{ hideAttribution: true }}
      // Pan canvas by dragging empty space; per-node drag would
      // intercept clicks on the AutoTuneTaskNode card surface, so the
      // selection toggle stops firing. Keep nodes pinned in place.
      panOnDrag={true}
      selectionOnDrag={false}
      nodesDraggable={false}
      // Let mouse-wheel scroll the page when the cursor is over the
      // canvas. ReactFlow's defaults capture wheel events for zoom +
      // pan, which makes the surrounding page feel un-scrollable. The
      // `+` / `-` buttons in <Controls /> still drive zoom; pinch-zoom
      // works for trackpad users.
      preventScrolling={false}
      zoomOnScroll={false}
      panOnScroll={false}
      zoomOnPinch={true}
    >
      {/* `showInteractive={false}` hides the lock toggle in the bottom
          corner — we don't expose drag/select interactivity so the
          control would be a no-op. */}
      <Controls showInteractive={false} />
    </Canvas>
  );
};

// eslint-disable-next-line react/no-multi-comp
const AutoTuneJobDag = (props: AutoTuneJobDagProps) => (
  <ReactFlowProvider>
    <AutoTuneJobDagInner {...props} />
  </ReactFlowProvider>
);

export default AutoTuneJobDag;
